import React, { useState } from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleNavLauncher } from "./ModuleNavLauncher";
import { ModuleConfig } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";
import { useAuth } from "../contexts/AuthContext";
import { LayoutChromeContext } from "./LayoutChromeContext";

/**
 * Runtime chrome control for pages inside ModulePageLayout. Now lives in its
 * own leaf file so a synced module can declare it as a shared file — this
 * layout component itself is tenant app-shell and can never be carried. See
 * `LayoutChromeContext.tsx` for the full reasoning. Re-exported here so the
 * old `from "./ModulePageLayout"` import path keeps working.
 */
export { LayoutChromeContext };

interface ModulePageLayoutProps {
  moduleConfig: ModuleConfig;
  children: React.ReactNode;
  /** Container maxW - defaults to "container.xl" */
  maxW?: string;
  /** Container py - defaults to 8 */
  py?: number;
  /** Skip Container wrapper entirely (for pages that manage their own layout) */
  noContainer?: boolean;
  /** Skip breadcrumb */
  noBreadcrumb?: boolean;
  /** Skip footer (full-bleed canvas pages) */
  noFooter?: boolean;
  /** Pin the frame to exactly 100dvh + overflow:hidden — the page owns its own
      internal scroll regions (e.g. knowledgeexplorer's 3 panes). */
  fillViewport?: boolean;
}

/**
 * ModulePageLayout — Shared layout wrapper for module pages.
 *
 * Provides consistent page scaffolding:
 *   1. NavbarWithCallToAction (top)
 *   2. ModuleNavLauncher with resolved permissions — the module's own navigation as a
 *      DRAGGABLE FLOATING LAUNCHER (vault/how-to/draggable-floating-launchers.md § Module-nav
 *      launcher), not the sticky ModuleBreadcrumb bar it replaced on 2026-08-27. The bar cost
 *      every module page a strip of screen whether or not the reader wanted to navigate.
 *      `noBreadcrumb` / `breadcrumbHidden` keep their names so every existing caller and
 *      runtime hide (calendar Fit mode, the calendars/emails nav launchers) works unchanged.
 *   3. Container wrapping page content (configurable maxW)
 *   4. FooterWithFourColumns (bottom, pushed down by flex)
 *
 * Usage: Set `useLayout: true` on routes in moduleConfig.ts.
 * App.tsx wraps those routes with this component automatically,
 * passing the RESOLVED moduleConfig (with real permissions from backend).
 *
 * Page components should return ONLY their content — no Navbar, Footer,
 * Breadcrumb, Container, or outer Box wrappers.
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  children,
  maxW = "container.xl",
  py = 8,
  noContainer = false,
  noBreadcrumb = false,
  noFooter = false,
  fillViewport = false,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const { isAuthenticated } = useAuth();
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);
  // The "↻ Reset floating panels" failsafe lives in FooterWithFourColumns
  // (Tom's call 2026-08-15: the footer is the one common place) — not here.

  return (
    <LayoutChromeContext.Provider value={{ setBreadcrumbHidden }}>
      <Box
        bg={bgMain}
        display="flex"
        flexDirection="column"
        {...(fillViewport ? { h: "100dvh", overflow: "hidden" } : { minHeight: "100vh" })}
      >
        <NavbarWithCallToAction />
        {/* Module navigation is authenticated — hide it for logged-out guests following a
            public shared link (e.g. /bill/:id), or when the page asked for it at runtime via
            LayoutChromeContext (calendar Fit mode; a module carrying its OWN nav launcher). */}
        {!noBreadcrumb && !breadcrumbHidden && isAuthenticated && <ModuleNavLauncher moduleConfig={moduleConfig} />}
        {noContainer ? (
          <Box
            flex="1"
            {...(fillViewport ? { minH: 0, overflow: "hidden", display: "flex", flexDirection: "column" } : {})}
          >
            {children}
          </Box>
        ) : (
          <Container maxW={maxW} py={py} flex="1">
            {children}
          </Container>
        )}
        {!noFooter && <FooterWithFourColumns />}
      </Box>
    </LayoutChromeContext.Provider>
  );
};
